import type { HeroProps } from '@/components/home/Hero';
import type { SuperChargeProps } from '@/components/home/SuperCharge/SuperCharge';
import type {
  CustomersReviewsProps,
  GenericProductsProps,
  HowItWorksProps,
  InstantEverythingProps,
  ProductsProps,
  TrustProps,
} from '@/components/shared';
import type { BasePageConfig } from '@/pageConfig/BasePageConfig';
import { sharedConfig } from '@/pageConfig/shared';
import aRated from '@/static/assets/home/a-rated.png';
import bCorp from '@/static/assets/home/b-corp.png';
import room from '@/static/assets/home/living-room-with-checkmarks.svg';
import nyse from '@/static/assets/home/nyse.png';
import pizzaDesktop from '@/static/assets/home/pizza_desktop_en-GB.svg';
import pizzaMobile1 from '@/static/assets/home/pizza_mobile_1.svg';
import pizzaMobile2 from '@/static/assets/home/pizza_mobile_2.svg';
import pizzaMobile3 from '@/static/assets/home/pizza_mobile_3.svg';
import ogImage from '@/static/og_image_uk.png';

// coverage
import type { CoverageProps } from '@/components/homeowners/coverage/Coverage';
// coverage uk
import damageMobile from '@/static/assets/uk_homepage/coverage//mobile/damage.svg';
import flamesMobile from '@/static/assets/uk_homepage/coverage//mobile/flames.svg';
import hurtMobile from '@/static/assets/uk_homepage/coverage//mobile/hurt.svg';
import waterMobile from '@/static/assets/uk_homepage/coverage//mobile/water.svg';
import breaks from '@/static/assets/uk_homepage/coverage/desktop/breaks.svg';
import damage from '@/static/assets/uk_homepage/coverage/desktop/damage.svg';
import flames from '@/static/assets/uk_homepage/coverage/desktop/flames.svg';
import hurt from '@/static/assets/uk_homepage/coverage/desktop/hurt.svg';
import water from '@/static/assets/uk_homepage/coverage/desktop/water.svg';
import weather from '@/static/assets/uk_homepage/coverage/desktop/weather.svg';
import breaksMobile from '@/static/assets/uk_homepage/coverage/mobile/breaks.svg';
import weatherMobile from '@/static/assets/uk_homepage/coverage/mobile/weather.svg';

// supercharge
import legal from '@/static/assets/supercharge/legal.svg';
import phone from '@/static/assets/supercharge/phone.svg';
import protection from '@/static/assets/supercharge/protection.svg';

const pizza = {
  desktop: pizzaDesktop,
  mobile: {
    1: pizzaMobile1,
    2: pizzaMobile2,
    3: pizzaMobile3,
  },
};

export interface Config extends BasePageConfig {
  hero: HeroProps;
  customersReviews: CustomersReviewsProps;
  products: ProductsProps;
  coverage: CoverageProps;
  genericProducts: GenericProductsProps;
  instantEverything: InstantEverythingProps;
  superCharge: SuperChargeProps;
  howItWorks: HowItWorksProps;
  trust: TrustProps;
}

export const config: Config = {
  seo: { ...sharedConfig['en-GB'].seo, ogImage: ogImage.src },
  layout: {
    ...sharedConfig['en-GB'].layout,
    showAskUsAnything: true,
    alignLogoOnMobile: 'left',
    headerActions: {
      ...sharedConfig['en-GB'].layout.headerActions,
      CTAs: {
        ...sharedConfig['en-GB'].layout.headerActions.CTAs,
        checkOurPrices: {
          ...sharedConfig['en-GB'].layout.headerActions.CTAs.checkOurPrices,
          short: {
            singular: 'Check our price',
            plural: 'Check our prices',
          },
        },
      },
    },
  },
  hero: {
    title: 'Forget everything you\nknow about insurance',
    subtitle: 'Keep your stuff safe from £4/mo* and your home from £14/mo**.',
    ctaLabel: 'Check our Prices',
    cart: {
      singular: 'Back to quote',
      plural: 'Back to quotes',
    },
    enableRef: false,
  },
  products: {
    title: 'Incredible prices. No paperwork. Cancel at any time.',
    trackingLabel: 'home',
    layout: {
      mt: [-10, -50],
    },
    cardsWidth: {
      desktop: 300,
    },
    products: [
      {
        title: 'Contents',
        subtitle: 'Cover for your stuff, both inside and outside of your home',
        price: 'From £4 a month',
        product: 'home_uk_contents',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
        alt: 'Contents Insurance',
      },
      {
        title: 'Buildings',
        subtitle: 'Protection for homeowners in case of structural damage or accidents',
        price: 'From £12 a month',
        product: 'home_uk_buildings',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
        alt: 'Homeowners Insurance',
      },
      {
        title: 'Buildings & Contents',
        subtitle: 'Protects you, your home and\nyour stuff',
        price: 'From £14 a month',
        product: 'home_uk_buildings_contents',
        ctaLabelDesktop: 'check our prices',
        ctaLabelMobile: 'check prices',
        alt: 'Car Insurance',
      },
    ],
    enableRef: true,
    darkBackground: true,
  },
  customersReviews: {
    title: 'Over 2,000,000 happy customers!',
    subtitle:
      'Lemonade is backed by some of the world’s largest reinsurers, and is publicly traded on the New York Stock Exchange',
    darkBackground: true,
    tweetsBackBandColor: '#f7f7f7',
  },
  coverage: {
    darkBackground: true,
    title: 'Get contents, buildings, or both for full cover',
    subtitle: 'Whether you rent or own, get the cover you actually need',
    data: [
      {
        title: "If there's a water leak",
        description:
          'Whether it’s a burst pipe or a leaking washing machine, we’ll cover finding and fixing the problem.',
        img: {
          src: {
            lg: water,
            sm: waterMobile,
          },
          alt: 'if there is a water leak',
        },
      },
      {
        title: 'If things get heated',
        description: 'Protect your stuff against fire or smoke damage.',
        img: {
          src: {
            lg: flames,
            sm: flamesMobile,
          },
          alt: 'If things get heated',
        },
      },
      {
        title: 'If someone breaks in',
        description: "We'll help replace or repair your stuff if it's damaged or stolen.",
        img: {
          src: {
            lg: breaks,
            sm: breaksMobile,
          },
          alt: 'if someone breaks in',
        },
      },
      {
        title: 'If your home is damaged',
        description: 'We’ll cover the cost of rebuilding your home.',
        img: {
          src: {
            lg: damage,
            sm: damageMobile,
          },
          alt: 'if your home is damaged',
        },
      },
      {
        title: ' If someone gets hurt',
        description:
          "In case of accidents you cause as the responsible owner of your home, we'll help pay for the damage.",
        img: {
          src: {
            lg: hurt,
            sm: hurtMobile,
          },
          alt: 'if someone gets hurt',
        },
      },
      {
        title: 'If bad weather hits',
        description: 'We cover damage to your home caused by floods, earthquakes, and storms.',
        img: {
          src: {
            lg: weather,
            sm: weatherMobile,
          },
          alt: 'If bad weather hits',
        },
      },
    ],
  },
  genericProducts: {
    title: 'Insurance<br/>Built For the 21st Century',
    subtitle: '100% digital. No paperwork. Sign up in seconds.',
    bgImage: room.src,
  },
  instantEverything: {
    title: 'Instant Everything',
    subtitle:
      'Maya, our charming artificial intelligence bot, will craft the perfect insurance for you.\nIt couldn’t be easier, or faster',
    appSketchContentLeft: 'Get insured<br/>in <b>seconds</b>',
    appSketchContentRight: 'Get paid<br/> in <b>Minutes</b>',
    darkBackground: true,
    adjustments: {
      contentSectionLeft: {
        left: { lg: '-425px' },
      },
      contentSectionRight: {
        right: { lg: '-415px' },
      },
    },
    layout: {
      mt: [-30, -50],
    },
  },
  superCharge: {
    title: 'Supercharge Your Insurance⚡',
    subtitle: 'Power up your policy with these add-ons',
    items: {
      left: {
        title: 'Protection Away<br/>From Home',
        subtitle: "Cover your bike, phone, laptop, and more while you're out and about",
        image: protection,
      },
      center: {
        title: 'Accidental Damage to<br/>Mobile Devices',
        subtitle: "If you unintentionally drop your laptop or phone, you're covered",
        image: phone,
      },
      right: {
        title: 'Legal<br/>Assistance Cover',
        subtitle: 'Get expert help and legal expenses cover for a wide range of legal issues',
        image: legal,
      },
    },
  },
  howItWorks: {
    title: 'How Lemonade Works',
    showBrandsImage: false,
    subtitle: `Lemonade reverses the traditional insurance model. We treat the premiums you pay as if it's your money, not ours. With Lemonade, everything becomes simple and transparent. We take a flat fee, pay claims super fast, and <a aria-labelledby="how-it-works-subtitle" href="/uk/giveback">give back</a> what’s left to causes you care about.^`,
    disclaimer: `^ Subject to board discretion and the company meeting certain financial standards. <a href="/uk/faq#giveback">Read more here</a>`,
    pizzaDesktop: pizza.desktop.src,
    pizzaMobile: [
      {
        image: pizza.mobile[1].src,
        text: 'A <b>flat fee</b> to </br> run everything',
      },
      {
        image: pizza.mobile[2].src,
        text: 'We pay claims <br/> <b>super fast</b>',
      },
      {
        image: pizza.mobile[3].src,
        text: `Good luck and good behavior let us <b>give back to causes</b>`,
      },
    ],
    darkBackground: true,
  },
  trust: {
    bulletsLayout: 'horizontal',
    bullets: [
      {
        name: 'social',
        title: 'Designed for Social Impact',
        text: 'Lemonade Inc. is a Public Benefit Corporation and certified B Corp. Social impact is part of our legal mission and business model - not just marketing fluff',
        image: {
          src: bCorp,
          width: 103,
          height: 88,
        },
      },
      {
        name: 'nyse',
        title: 'NYSE:LMND',
        text: 'Lemonade is a publicly traded company, listed on the New York Stock Exchange under the LMND stock symbol',
        image: {
          src: nyse,
          width: 100,
          height: 89,
        },
      },
      {
        name: 'rating',
        title: 'A-Rated and Backed by Giants',
        text: 'Lemonade is rated A-Exceptional, fully licensed, and reinsured by some of the most trusted names on the planet',
        image: {
          src: aRated,
          width: 100,
          height: 89,
        },
      },
    ],
  },
};
